import { useEffect, useState, useMemo, type CSSProperties, type FormEvent } from "react";
import {
  listBottleEnrichmentQueue,
  listDistilleryEnrichmentQueue,
  saveBottleSpecs,
  saveDistillerySpecs,
  type BottleEnrichmentQueueItem,
  type DistilleryEnrichmentQueueItem,
} from "../../lib/api";
import WhiskeyStudioTabs from "../../components/admin/WhiskeyStudioTabs";

export default function AdminEnrichment() {
  const [activeTab, setActiveTab] = useState<"bottles" | "distilleries">("bottles");
  const [bottles, setBottles] = useState<BottleEnrichmentQueueItem[]>([]);
  const [distilleries, setDistilleries] = useState<DistilleryEnrichmentQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMissing, setFilterMissing] = useState<"all" | "missing_desc" | "missing_mash" | "missing_abv">("all");

  // Edit Modal State
  const [editingBottle, setEditingBottle] = useState<BottleEnrichmentQueueItem | null>(null);
  const [editingDistillery, setEditingDistillery] = useState<DistilleryEnrichmentQueueItem | null>(null);
  const [saving, setSaving] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setEditingBottle(null);
        setEditingDistillery(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === "bottles") {
        const bList = await listBottleEnrichmentQueue();
        setBottles(bList || []);
      } else {
        const dList = await listDistilleryEnrichmentQueue();
        setDistilleries(dList || []);
      }
    } catch (err: any) {
      showToast(err.message || "Failed to load enrichment queue", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const filteredBottles = useMemo(() => {
    let list = bottles;
    if (filterMissing === "missing_desc") list = list.filter((b) => !b.description);
    if (filterMissing === "missing_mash") list = list.filter((b) => !b.mashbill);
    if (filterMissing === "missing_abv") list = list.filter((b) => b.abv == null);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (b) =>
          b.name.toLowerCase().includes(q) ||
          (b.subcategory && b.subcategory.toLowerCase().includes(q))
      );
    }
    return list;
  }, [bottles, filterMissing, searchQuery]);

  const filteredDistilleries = useMemo(() => {
    let list = distilleries;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.state && d.state.toLowerCase().includes(q)) ||
          (d.region && d.region.toLowerCase().includes(q))
      );
    }
    return list;
  }, [distilleries, searchQuery]);

  const handleSaveBottle = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingBottle) return;
    setSaving(true);
    try {
      await saveBottleSpecs(editingBottle.id, {
        description: editingBottle.description || null,
        mashbill: editingBottle.mashbill || null,
        abv: editingBottle.abv ? Number(editingBottle.abv) : null,
        age_years: editingBottle.age_years ? Number(editingBottle.age_years) : null,
      });
      showToast(`✓ Updated specs for "${editingBottle.name}"`);
      setEditingBottle(null);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to save bottle specs", "err");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDistillery = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingDistillery) return;
    setSaving(true);
    try {
      await saveDistillerySpecs(editingDistillery.id, {
        region: editingDistillery.region || null,
        state: editingDistillery.state || null,
        founded_year: editingDistillery.founded_year ? Number(editingDistillery.founded_year) : null,
        master_distiller: editingDistillery.master_distiller || null,
        website_url: editingDistillery.website_url || null,
        story: editingDistillery.story || null,
      });
      showToast(`✓ Updated distillery "${editingDistillery.name}"`);
      setEditingDistillery(null);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to save distillery", "err");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.container}>
      <WhiskeyStudioTabs activeTab="enrichment" badgeCounts={{ enrichment: bottles.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Catalog &amp; Distillery AI Enrichment</h1>
          <p style={styles.subtitle}>
            Enrich bottle mashbills, ABV, age statements, distillery provenance, and master distillers.
          </p>
        </div>
      </div>

      {/* Mode Tabs */}
      <div style={styles.tabBar}>
        <button
          type="button"
          style={activeTab === "bottles" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("bottles")}
        >
          Bottles &amp; Spirits Specs ({bottles.length})
        </button>
        <button
          type="button"
          style={activeTab === "distilleries" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("distilleries")}
        >
          Distillery Profiles &amp; Provenance ({distilleries.length})
        </button>
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder={activeTab === "bottles" ? "Search bottles by name, category…" : "Search distilleries…"}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        {activeTab === "bottles" && (
          <select
            value={filterMissing}
            onChange={(e) => setFilterMissing(e.target.value as any)}
            style={styles.select}
          >
            <option value="all">All Spirits</option>
            <option value="missing_desc">Missing Tasting Description</option>
            <option value="missing_mash">Missing Mashbill</option>
            <option value="missing_abv">Missing Proof / ABV</option>
          </select>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div style={styles.loader}>Loading enrichment catalog…</div>
      ) : activeTab === "bottles" ? (
        filteredBottles.length === 0 ? (
          <div style={styles.emptyState}>No spirits found in this queue.</div>
        ) : (
          <div style={styles.grid}>
            {filteredBottles.map((b) => {
              const hasMash = !!b.mashbill;
              const hasDesc = !!b.description;
              const hasAbv = b.abv != null;
              return (
                <div
                  key={b.id}
                  style={styles.card}
                  onClick={() => setEditingBottle({ ...b })}
                >
                  <div style={styles.cardHeader}>
                    <span style={styles.subcatBadge}>{b.subcategory || b.category_slug || "Spirit"}</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      <span style={{ ...styles.pill, color: hasMash ? "#27ae60" : "#E05040" }}>
                        {hasMash ? "✓ Mash" : "✗ Mash"}
                      </span>
                      <span style={{ ...styles.pill, color: hasDesc ? "#27ae60" : "#E05040" }}>
                        {hasDesc ? "✓ Desc" : "✗ Desc"}
                      </span>
                      <span style={{ ...styles.pill, color: hasAbv ? "#27ae60" : "#E05040" }}>
                        {hasAbv ? "✓ ABV" : "✗ ABV"}
                      </span>
                    </div>
                  </div>

                  <h3 style={styles.cardTitle}>{b.name}</h3>
                  <div style={styles.specsRow}>
                    <span>ABV: <strong>{b.abv ? `${b.abv}%` : "—"}</strong></span>
                    <span>Age: <strong>{b.age_years ? `${b.age_years} yr` : "NAS"}</strong></span>
                    {b.mashbill && <span>Mash: <em>{b.mashbill}</em></span>}
                  </div>

                  <div style={styles.cardFooter}>
                    <span style={{ color: "var(--muted)", fontSize: 11 }}>{b.has_image ? "📷 Photo linked" : "No photo"}</span>
                    <span style={{ color: "var(--gold)", fontWeight: 700, fontSize: 11 }}>Enrich Specs →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : filteredDistilleries.length === 0 ? (
        <div style={styles.emptyState}>No distilleries found matching criteria.</div>
      ) : (
        <div style={styles.grid}>
          {filteredDistilleries.map((d) => (
            <div
              key={d.id}
              style={styles.card}
              onClick={() => setEditingDistillery({ ...d })}
            >
              <div style={styles.cardHeader}>
                <span style={styles.subcatBadge}>{d.region || d.state || "Distillery"}</span>
                <span style={{ ...styles.pill, color: d.has_location ? "#27ae60" : "#E05040" }}>
                  {d.has_location ? "✓ Geo Map" : "✗ No Location"}
                </span>
              </div>

              <h3 style={styles.cardTitle}>{d.name}</h3>
              <div style={styles.specsRow}>
                <span>Founded: <strong>{d.founded_year || "—"}</strong></span>
                <span>Master Distiller: <strong>{d.master_distiller || "—"}</strong></span>
              </div>

              <div style={styles.cardFooter}>
                <span style={{ color: "var(--muted)", fontSize: 11 }}>{d.website_url ? "🌐 Website" : "No site"}</span>
                <span style={{ color: "var(--gold)", fontWeight: 700, fontSize: 11 }}>Enrich Story &amp; Details →</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bottle Edit Modal */}
      {editingBottle && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Enrich Bottle Specs: {editingBottle.name}</h2>
              <button type="button" style={styles.btnIcon} onClick={() => setEditingBottle(null)}>&times;</button>
            </div>
            <form onSubmit={handleSaveBottle} style={styles.modalForm}>
              <div className="station-form-grid" style={styles.formGrid}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>ABV (% Proof / 2)</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="e.g. 45.0"
                    value={editingBottle.abv ?? ""}
                    onChange={(e) => setEditingBottle({ ...editingBottle, abv: e.target.value ? Number(e.target.value) : null })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Age Statement (Years)</label>
                  <input
                    type="number"
                    placeholder="e.g. 10"
                    value={editingBottle.age_years ?? ""}
                    onChange={(e) => setEditingBottle({ ...editingBottle, age_years: e.target.value ? Number(e.target.value) : null })}
                    style={styles.input}
                  />
                </div>
                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Grain Mashbill Recipe</label>
                  <input
                    type="text"
                    placeholder="e.g. 75% Corn, 21% Rye, 4% Malted Barley"
                    value={editingBottle.mashbill || ""}
                    onChange={(e) => setEditingBottle({ ...editingBottle, mashbill: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Tasting Notes &amp; Flavor Profile</label>
                  <textarea
                    rows={4}
                    placeholder="Rich caramel, toasted oak, leather, dark cherry..."
                    value={editingBottle.description || ""}
                    onChange={(e) => setEditingBottle({ ...editingBottle, description: e.target.value })}
                    style={styles.textarea}
                  />
                </div>
              </div>
              <div style={styles.modalActions}>
                <button type="button" style={styles.btnSecondary} onClick={() => setEditingBottle(null)}>Cancel</button>
                <button type="submit" style={styles.btnPrimary} disabled={saving}>
                  {saving ? "Saving…" : "Save Specs"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Distillery Edit Modal */}
      {editingDistillery && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Enrich Distillery: {editingDistillery.name}</h2>
              <button type="button" style={styles.btnIcon} onClick={() => setEditingDistillery(null)}>&times;</button>
            </div>
            <form onSubmit={handleSaveDistillery} style={styles.modalForm}>
              <div className="station-form-grid" style={styles.formGrid}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>State / Province</label>
                  <input
                    type="text"
                    placeholder="e.g. Kentucky"
                    value={editingDistillery.state || ""}
                    onChange={(e) => setEditingDistillery({ ...editingDistillery, state: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Region</label>
                  <input
                    type="text"
                    placeholder="e.g. Bourbon County"
                    value={editingDistillery.region || ""}
                    onChange={(e) => setEditingDistillery({ ...editingDistillery, region: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Founded Year</label>
                  <input
                    type="number"
                    placeholder="e.g. 1870"
                    value={editingDistillery.founded_year ?? ""}
                    onChange={(e) => setEditingDistillery({ ...editingDistillery, founded_year: e.target.value ? Number(e.target.value) : null })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Master Distiller</label>
                  <input
                    type="text"
                    placeholder="e.g. Eddie Russell"
                    value={editingDistillery.master_distiller || ""}
                    onChange={(e) => setEditingDistillery({ ...editingDistillery, master_distiller: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Official Website URL</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={editingDistillery.website_url || ""}
                    onChange={(e) => setEditingDistillery({ ...editingDistillery, website_url: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Heritage Story &amp; Distillation Method</label>
                  <textarea
                    rows={4}
                    placeholder="Distillery heritage, water source, copper pot stills..."
                    value={editingDistillery.story || ""}
                    onChange={(e) => setEditingDistillery({ ...editingDistillery, story: e.target.value })}
                    style={styles.textarea}
                  />
                </div>
              </div>
              <div style={styles.modalActions}>
                <button type="button" style={styles.btnSecondary} onClick={() => setEditingDistillery(null)}>Cancel</button>
                <button type="submit" style={styles.btnPrimary} disabled={saving}>
                  {saving ? "Saving…" : "Save Distillery"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  tabBar: { display: "flex", gap: 10, borderBottom: "1px solid var(--border)", marginBottom: 20 },
  tab: { background: "none", border: "none", borderBottom: "2px solid transparent", color: "var(--muted)", padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  tabActive: { background: "none", border: "none", borderBottom: "2px solid var(--gold)", color: "var(--gold)", padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 },
  searchInput: { flex: 1, minWidth: 260, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  select: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 },
  card: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, cursor: "pointer", display: "flex", flexDirection: "column", gap: 8 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  subcatBadge: { fontSize: 10, fontWeight: 800, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5 },
  pill: { fontSize: 10, fontWeight: 800, padding: "1px 5px", background: "rgba(0,0,0,0.2)", borderRadius: 4 },
  cardTitle: { fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)" },
  specsRow: { display: "flex", gap: 12, fontSize: 12, color: "var(--muted)", flexWrap: "wrap" },
  cardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: "auto" },
  btnPrimary: { background: "var(--gold)", color: "#111", border: 0, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  btnSecondary: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnIcon: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 22, cursor: "pointer" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modalContent: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  modalForm: { padding: 20, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", letterSpacing: 0.5 },
  input: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  textarea: { width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "10px 12px", fontSize: 13, resize: "vertical" },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid var(--border)", paddingTop: 16 },
};
